import React, { useState } from 'react';
import {
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  browserLocalPersistence,
  browserSessionPersistence,
  setPersistence,
  getMultiFactorResolver,
  OAuthProvider,
  signInWithCredential,
} from 'firebase/auth';
import { sendePasswortReset } from '../api';
import { capture } from '../tracking/posthog';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { auth, googleProvider } from '../firebase';
import { sendVerificationEmail, speichereOnboardingProfil, createProfil, getProfile, BASE_URL as API_BASE_URL } from '../api';
import { takePreLoginFile, queueCvImport } from '../pendingCvImport';
import { istWegwerfEmail } from '../utils/emailValidator';
import MFAChallenge from './MFAChallenge';
import TurnstileWidget, { istTurnstileAktiv } from './TurnstileWidget';
import './Login.css';

const _LI_CLIENT_ID = process.env.REACT_APP_LINKEDIN_CLIENT_ID || '';
const _LI_SCOPE = 'openid profile email';
const _API_BASE = API_BASE_URL;
const ONBOARDING_DRAFT_KEY = 'cvflow_onboarding_draft';
const ONBOARDING_SEED_KEY = 'cvflow_onboarding_profile_seed';

const FIREBASE_ERRORS = {
  'auth/email-already-in-use':         'Diese E-Mail-Adresse ist bereits registriert.',
  'auth/wrong-password':               'E-Mail oder Passwort ist falsch.',
  'auth/invalid-credential':           'E-Mail oder Passwort ist falsch.',
  'auth/user-not-found':               'Kein Konto mit dieser E-Mail-Adresse gefunden.',
  'auth/invalid-email':                'Bitte gib eine gültige E-Mail-Adresse ein.',
  'auth/weak-password':                'Das Passwort muss mindestens 6 Zeichen lang sein.',
  'auth/too-many-requests':            'Zu viele Versuche. Bitte warte kurz und versuche es erneut.',
  'auth/network-request-failed':       'Netzwerkfehler. Bitte überprüfe deine Internetverbindung.',
  'auth/operation-not-allowed':        'Diese Anmeldemethode ist nicht aktiviert. Bitte kontaktiere den Support.',
  'auth/popup-closed-by-user':         null, // ignore silently
  'auth/multi-factor-auth-required':   null, // handled via MFAChallenge
  'auth/account-exists-with-different-credential': 'Ein Konto mit dieser E-Mail existiert bereits. Bitte melde dich mit deiner ursprünglichen Anmeldemethode an (z.B. Google).',
};

const PW_REGELN = [
  { id: 'len',   label: 'Mindestens 8 Zeichen',  test: pw => pw.length >= 8 },
  { id: 'upper', label: '1 Großbuchstabe',        test: pw => /[A-Z]/.test(pw) },
  { id: 'lower', label: '1 Kleinbuchstabe',       test: pw => /[a-z]/.test(pw) },
  { id: 'digit', label: '1 Zahl',                 test: pw => /[0-9]/.test(pw) },
  { id: 'spec',  label: '1 Sonderzeichen',        test: pw => /[^A-Za-z0-9]/.test(pw) },
];

function passwortScore(pw) {
  if (!pw) return 0;
  return PW_REGELN.filter(r => r.test(pw)).length;
}

function PasswortAnforderungen({ passwort }) {
  if (!passwort) return null;
  return (
    <ul className="pw-anforderungen">
      {PW_REGELN.map(r => {
        const ok = r.test(passwort);
        return (
          <li key={r.id} className={ok ? 'pw-req-ok' : 'pw-req-fehlt'}>
            <span className="pw-req-icon">{ok ? '✓' : '✕'}</span>
            {r.label}
          </li>
        );
      })}
    </ul>
  );
}

export default function Login() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const darfRegistrieren = params.get('mode') === 'register';
  const initialView = darfRegistrieren ? 'register' : 'login';
  const [view, setView]             = useState(initialView); // 'login' | 'register' | 'reset'
  const [email, setEmail]           = useState('');
  const [passwort, setPasswort]     = useState('');
  const [merken, setMerken]         = useState(true);
  const [pwSichtbar, setPwSichtbar] = useState(false);
  const [laden, setLaden]           = useState(false);
  const [fehler, setFehler]         = useState(null);
  const [erfolg, setErfolg]         = useState(null);
  const [mfaResolver, setMfaResolver]         = useState(null);
  const [turnstileToken, setTurnstileToken]   = useState(null);

  const resetForm = () => {
    setEmail(''); setPasswort('');
    setFehler(null); setErfolg(null); setPwSichtbar(false); setTurnstileToken(null);
  };

  const wechseln = (neueView) => { resetForm(); setView(neueView); };

  const fehlerText = (code) => FIREBASE_ERRORS[code] !== undefined
    ? FIREBASE_ERRORS[code]
    : 'Ein unbekannter Fehler ist aufgetreten.';

  const onboardingNachAuthAbschliessen = async () => {
    const raw = localStorage.getItem(ONBOARDING_DRAFT_KEY);
    if (!raw) return false;
    try {
      // Landing-Page-Onboarding gehört nur zu einem brandneuen Account. Loggt sich
      // stattdessen jemand in einen bestehenden Account ein, darf der liegengebliebene
      // Entwurf dessen Profil nicht überschreiben oder das Ersteinrichtungs-Fenster auslösen.
      const bestehendeProfile = await getProfile();
      if (bestehendeProfile.data.length > 0) {
        localStorage.removeItem(ONBOARDING_DRAFT_KEY);
        localStorage.removeItem(ONBOARDING_SEED_KEY);
        return false;
      }

      const draft = JSON.parse(raw);
      const antworten = draft?.antworten;
      if (!antworten) return false;
      await speichereOnboardingProfil(antworten);

      // Profil anlegen + CV importieren (analog zu OnboardingWizard.onboardingAbschliessen)
      const stammdaten = antworten.stammdaten;
      if (stammdaten) {
        const payload = Object.fromEntries(
          Object.entries(stammdaten)
            .filter(([k]) => k !== 'cvDatei')
            .map(([k, v]) => [k, String(v || '').trim()])
        );
        let dateiname = null;
        try {
          const res = await createProfil(payload);
          dateiname = res.data.dateiname;
        } catch (err) {
          if (err?.response?.status === 409) {
            dateiname = `${payload.vorname}_${payload.nachname}`.toLowerCase().replace(/ /g, '_');
          }
        }
        if (dateiname) {
          const uid = auth.currentUser?.uid;
          const setupKey = uid
            ? `cvflow_first_profile_setup_done:${uid}`
            : 'cvflow_first_profile_setup_done';
          const file = takePreLoginFile();
          if (file) {
            queueCvImport(dateiname, file, setupKey);
          } else {
            localStorage.setItem(setupKey, '1');
          }
        }
      }

      localStorage.setItem('onboarding_done', '1');
      localStorage.setItem(ONBOARDING_SEED_KEY, JSON.stringify(antworten));
      localStorage.removeItem(ONBOARDING_DRAFT_KEY);
      navigate('/dashboard', { replace: true });
      return true;
    } catch {
      setFehler('Konto erstellt, aber dein Onboarding konnte nicht gespeichert werden. Bitte versuche es erneut.');
      return true;
    }
  };

  // ── Login ────────────────────────────────────────
  const onLoginEmail = async (e) => {
    e.preventDefault();
    setFehler(null);
    if (istTurnstileAktiv && !turnstileToken) {
      setFehler('Bitte bestätige, dass du kein Bot bist.');
      return;
    }
    setLaden(true);
    try {
      if (istTurnstileAktiv) {
        const verifyResp = await fetch(`${_API_BASE}/auth/turnstile-verify`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: turnstileToken, email, action: 'login' }),
        });
        if (!verifyResp.ok) {
          setFehler('Bot-Schutz fehlgeschlagen. Bitte lade die Seite neu und versuche es erneut.');
          return;
        }
      }
      await setPersistence(auth, merken ? browserLocalPersistence : browserSessionPersistence);
      const cred = await signInWithEmailAndPassword(auth, email, passwort);
      if (cred.user.providerData?.[0]?.providerId === 'password' && !cred.user.emailVerified) {
        navigate('/dashboard', { replace: true });
        return;
      }
      await onboardingNachAuthAbschliessen();
    } catch (err) {
      if (err.code === 'auth/multi-factor-auth-required') {
        setMfaResolver(getMultiFactorResolver(auth, err));
      } else {
        const msg = fehlerText(err.code);
        if (msg) setFehler(msg);
      }
    } finally {
      setLaden(false);
    }
  };

  // ── Register ─────────────────────────────────────
  const onRegister = async (e) => {
    e.preventDefault();
    capture('signup_method_selected', { method: 'email' });
    setFehler(null);
    if (passwortScore(passwort) < 5) {
      setFehler('Das Passwort erfüllt nicht alle Anforderungen.');
      return;
    }
    if (istWegwerfEmail(email)) {
      setFehler('Bitte verwende eine echte E-Mail-Adresse — Wegwerf-Mails werden nicht akzeptiert.');
      return;
    }
    if (istTurnstileAktiv && !turnstileToken) {
      setFehler('Bitte bestätige, dass du kein Bot bist.');
      return;
    }
    setLaden(true);
    try {
      if (istTurnstileAktiv) {
        const verifyResp = await fetch(`${_API_BASE}/auth/turnstile-verify`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: turnstileToken, email, action: 'signup' }),
        });
        if (!verifyResp.ok) {
          setFehler('Bot-Schutz fehlgeschlagen. Bitte lade die Seite neu und versuche es erneut.');
          return;
        }
      }
      const cred = await createUserWithEmailAndPassword(auth, email, passwort);
      capture('signup_completed', { method: 'email' });
      try {
        const draft = JSON.parse(localStorage.getItem(ONBOARDING_DRAFT_KEY) || '{}');
        if (draft?.antworten) {
          localStorage.setItem(ONBOARDING_SEED_KEY, JSON.stringify(draft.antworten));
        }
      } catch {}
      try {
        await cred.user.getIdToken(true);
        await sendVerificationEmail();
      } catch {}
      navigate('/dashboard', { replace: true });
      // App.js detects emailVerified=false → shows EmailVerification screen.
      // The onboarding draft remains local and is saved after verification.
    } catch (err) {
      const msg = fehlerText(err.code);
      if (msg) setFehler(msg);
    } finally {
      setLaden(false);
    }
  };

  // ── Google ───────────────────────────────────────
  const onGoogleLogin = () => {
    if (laden) return;
    capture('signup_method_selected', { method: 'google' });
    setFehler(null);
    setLaden(true);
    signInWithPopup(auth, googleProvider)
      .catch((err) => {
        if (err.code === 'auth/multi-factor-auth-required') {
          setMfaResolver(getMultiFactorResolver(auth, err));
        } else if (err.code === 'auth/popup-blocked') {
          setFehler('Popup wurde vom Browser blockiert. Bitte erlaube Popups für diese Seite und versuche es erneut.');
        } else {
          const msg = fehlerText(err.code);
          if (msg) setFehler(msg);
        }
      })
      .finally(() => setLaden(false));
  };

  // ── LinkedIn ─────────────────────────────────────
  const onLinkedInLogin = () => {
    if (laden) return;
    capture('signup_method_selected', { method: 'linkedin' });
    setFehler(null);
    setLaden(true);

    const state = crypto.randomUUID();
    const redirectUri = `${window.location.origin}/linkedin-callback`;
    const authUrl = new URL('https://www.linkedin.com/oauth/v2/authorization');
    authUrl.searchParams.set('response_type', 'code');
    authUrl.searchParams.set('client_id', _LI_CLIENT_ID);
    authUrl.searchParams.set('redirect_uri', redirectUri);
    authUrl.searchParams.set('scope', _LI_SCOPE);
    authUrl.searchParams.set('state', state);

    const popup = window.open(authUrl.toString(), 'linkedin-auth', 'width=600,height=700,left=200,top=100');

    if (!popup) {
      setFehler('Popup wurde vom Browser blockiert. Bitte erlaube Popups für diese Seite.');
      setLaden(false);
      return;
    }

    const handleMessage = async (event) => {
      if (event.origin !== window.location.origin) return;
      if (!event.data || event.data.type !== 'LINKEDIN_AUTH') return;

      window.removeEventListener('message', handleMessage);
      clearInterval(closedCheck);

      const { code, state: returnedState, error } = event.data;

      if (error || !code) {
        setFehler(null); // Benutzer hat Popup geschlossen
        setLaden(false);
        return;
      }

      if (returnedState !== state) {
        setFehler('Sicherheitsfehler bei der LinkedIn-Anmeldung. Bitte versuche es erneut.');
        setLaden(false);
        return;
      }

      try {
        const resp = await fetch(`${_API_BASE}/auth/linkedin/token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code, redirect_uri: redirectUri }),
        });
        if (!resp.ok) throw new Error(`Token exchange ${resp.status}`);
        const { id_token } = await resp.json();

        const provider = new OAuthProvider('oidc.linkedin');
        const credential = provider.credential({ idToken: id_token });
        await signInWithCredential(auth, credential);
      } catch (err) {
        console.error('[LinkedIn Login]', err);
        setFehler('LinkedIn-Anmeldung fehlgeschlagen. Bitte versuche es erneut.');
      } finally {
        setLaden(false);
      }
    };

    window.addEventListener('message', handleMessage);

    const closedCheck = setInterval(() => {
      if (popup.closed) {
        clearInterval(closedCheck);
        window.removeEventListener('message', handleMessage);
        setLaden(false);
      }
    }, 500);
  };

  // ── Password Reset ───────────────────────────────
  const onPasswortReset = async (e) => {
    e.preventDefault();
    setFehler(null); setErfolg(null);
    setLaden(true);
    try {
      await sendePasswortReset(email);
      setErfolg('Wir haben dir einen Link zum Zurücksetzen deines Passworts geschickt. Bitte überprüfe dein Postfach.');
    } catch (err) {
      const msg = fehlerText(err.code);
      if (msg) setFehler(msg);
    } finally {
      setLaden(false);
    }
  };

  if (mfaResolver) {
    return <MFAChallenge resolver={mfaResolver} onAbbrechen={() => setMfaResolver(null)} />;
  }

  return (
    <div className="auth-root">
      {/* ── Brand Panel ── */}
      <div className="auth-brand">
        <img src="/logo.png" alt="" className="auth-brand-deco" aria-hidden="true" />

        <Link to="/" className="auth-logo">
          <img src="/logo.png" alt="CVFlow" className="auth-logo-mark" />
        </Link>

        <div className="auth-brand-center">
          <h2 className="auth-brand-headline">
            Stundenlange Arbeit,<br />erledigt in Sekunden.
          </h2>
          <p className="auth-brand-sub">
            Link zur Stellenanzeige einfügen und fertig. Wir automatisieren die Analyse, die Formulierung und den Faktencheck für dich.
          </p>
          <div className="auth-features">
            <div className="auth-feature">
              <div className="auth-feature-text">
                <strong>Vom Job-Link zur fertigen Bewerbung.</strong>
                <span>CVFlow analysiert die Stelle, optimiert deinen Lebenslauf, formuliert dein Anschreiben und prüft deine Angaben auf Konsistenz.</span>
              </div>
            </div>
            <div className="auth-feature">
              <div className="auth-feature-text">
                <strong>Schneller aus dem Bewerbungs-Stau.</strong>
                <span>Keine leere Seite, kein Formatierungschaos, kein Hin-und-her zwischen Tools. Du startest mit einem starken Entwurf.</span>
              </div>
            </div>
            <div className="auth-feature">
              <div className="auth-feature-text">
                <strong>Vertraulich by design.</strong>
                <span>Deine Bewerbungsdaten werden verschlüsselt verarbeitet und auf EU-Servern gespeichert. DSGVO-konform, nachvollziehbar, kontrollierbar.</span>
              </div>
            </div>
          </div>
        </div>

        <div className="auth-brand-footer">
          Made in Germany ·{' '}
          <Link to="/impressum">Impressum</Link>
          {' '}·{' '}
          <Link to="/agb">AGB</Link>
          {' '}·{' '}
          <Link to="/datenschutz">Datenschutz</Link>
        </div>
      </div>

      {/* ── Form Panel ── */}
      <div className="auth-form-panel">

        {/* Mobile-only brand header */}
        <div className="auth-mobile-brand" aria-hidden="true">
          <Link to="/" className="auth-mobile-logo">
            <img src="/logo.png" alt="CVFlow" />
          </Link>
          <p className="auth-mobile-tagline">Stundenlange Arbeit,<br />erledigt in Sekunden.</p>
          <img src="/logo.png" alt="" className="auth-mobile-deco" />
        </div>

        <div className="auth-form-inner">

          {/* ── Password Reset View ── */}
          {view === 'reset' ? (
            <>
              <button className="auth-reset-back" onClick={() => wechseln('login')}>
                ← Zurück zum Login
              </button>
              <h1 className="auth-heading">Passwort zurücksetzen</h1>
              <p className="auth-subheading">Wir schicken dir einen Reset-Link per E-Mail.</p>

              {fehler  && <div className="auth-error">{fehler}</div>}
              {erfolg  && <div className="auth-success">{erfolg}</div>}

              {!erfolg && (
                <form onSubmit={onPasswortReset}>
                  <div className="auth-field">
                    <label htmlFor="reset-email">E-Mail-Adresse</label>
                    <div className="auth-input-wrap">
                      <input
                        id="reset-email"
                        type="email"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        placeholder="du@beispiel.de"
                        required
                        autoFocus
                      />
                    </div>
                  </div>
                  <button type="submit" className="auth-submit" disabled={laden}>
                    {laden ? <span className="auth-spinner" /> : 'Link senden'}
                  </button>
                </form>
              )}
            </>
          ) : (
            /* ── Login / Register Views ── */
            <>
              {darfRegistrieren && (
                <div className="auth-view-tabs">
                  <button
                    className={`auth-tab${view === 'login' ? ' aktiv' : ''}`}
                    onClick={() => wechseln('login')}
                  >
                    Einloggen
                  </button>
                  <button
                    className={`auth-tab${view === 'register' ? ' aktiv' : ''}`}
                    onClick={() => wechseln('register')}
                  >
                    Registrieren
                  </button>
                </div>
              )}

              <h1 className="auth-heading">
                {view === 'login' ? 'Willkommen zurück' : 'Konto erstellen'}
              </h1>
              <p className="auth-subheading">
                {view === 'login'
                  ? "Melde dich an und weiter geht's."
                  : 'Kostenlos starten — kein Abo, kein Spam.'}
              </p>

              {fehler && <div className="auth-error">{fehler}</div>}

              <form onSubmit={view === 'login' ? onLoginEmail : onRegister}>
                {/* E-Mail */}
                <div className="auth-field">
                  <label htmlFor="auth-email">E-Mail-Adresse</label>
                  <div className="auth-input-wrap">
                    <input
                      id="auth-email"
                      type="email"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      placeholder="du@beispiel.de"
                      required
                      autoFocus
                    />
                  </div>
                </div>

                {/* Passwort */}
                <div className="auth-field">
                  <label htmlFor="auth-pw">Passwort</label>
                  <div className="auth-input-wrap">
                    <input
                      id="auth-pw"
                      type={pwSichtbar ? 'text' : 'password'}
                      value={passwort}
                      onChange={e => setPasswort(e.target.value)}
                      placeholder="••••••••"
                      required
                    />
                    <button
                      type="button"
                      className="auth-pw-toggle"
                      onClick={() => setPwSichtbar(v => !v)}
                      tabIndex={-1}
                      aria-label={pwSichtbar ? 'Passwort verbergen' : 'Passwort anzeigen'}
                    >
                      {pwSichtbar ? (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <g clipPath="url(#clip_eye_off)">
                            <path d="M14.53 9.46992L9.47004 14.5299C8.82004 13.8799 8.42004 12.9899 8.42004 11.9999C8.42004 10.0199 10.02 8.41992 12 8.41992C12.99 8.41992 13.88 8.81992 14.53 9.46992Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M17.82 5.77047C16.07 4.45047 14.07 3.73047 12 3.73047C8.46997 3.73047 5.17997 5.81047 2.88997 9.41047C1.98997 10.8205 1.98997 13.1905 2.88997 14.6005C3.67997 15.8405 4.59997 16.9105 5.59997 17.7705" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M8.42004 19.5297C9.56004 20.0097 10.77 20.2697 12 20.2697C15.53 20.2697 18.82 18.1897 21.11 14.5897C22.01 13.1797 22.01 10.8097 21.11 9.39969C20.78 8.87969 20.42 8.38969 20.05 7.92969" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M15.5099 12.6992C15.2499 14.1092 14.0999 15.2592 12.6899 15.5192" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M9.47 14.5293L2 21.9993" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M22 2L14.53 9.47" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </g>
                          <defs>
                            <clipPath id="clip_eye_off">
                              <rect width="24" height="24" fill="white"/>
                            </clipPath>
                          </defs>
                        </svg>
                      ) : (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <g clipPath="url(#clip_eye_on)">
                            <path d="M15.58 11.9999C15.58 13.9799 13.98 15.5799 12 15.5799C10.02 15.5799 8.42004 13.9799 8.42004 11.9999C8.42004 10.0199 10.02 8.41992 12 8.41992C13.98 8.41992 15.58 10.0199 15.58 11.9999Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M12 20.2707C15.53 20.2707 18.82 18.1907 21.11 14.5907C22.01 13.1807 22.01 10.8107 21.11 9.4007C18.82 5.8007 15.53 3.7207 12 3.7207C8.46997 3.7207 5.17997 5.8007 2.88997 9.4007C1.98997 10.8107 1.98997 13.1807 2.88997 14.5907C5.17997 18.1907 8.46997 20.2707 12 20.2707Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </g>
                          <defs>
                            <clipPath id="clip_eye_on">
                              <rect width="24" height="24" fill="white"/>
                            </clipPath>
                          </defs>
                        </svg>
                      )}
                    </button>
                  </div>
                  {view === 'register' && <PasswortAnforderungen passwort={passwort} />}
                </div>

                {/* Angemeldet bleiben (nur Login) */}
                {view === 'login' && (
                  <div className="auth-remember">
                    <input
                      type="checkbox"
                      id="auth-merken"
                      checked={merken}
                      onChange={e => setMerken(e.target.checked)}
                    />
                    <label htmlFor="auth-merken">Angemeldet bleiben</label>
                  </div>
                )}

                {/* Turnstile Bot-Schutz (Login + Register) */}
                <TurnstileWidget
                  onToken={setTurnstileToken}
                  onError={() => setFehler('Bot-Schutz fehlgeschlagen. Bitte lade die Seite neu.')}
                />

                {/* AGB (nur Register) */}
                {view === 'register' && (
                  <p className="auth-agb-hinweis">
                    Mit der Registrierung stimmst du unseren{' '}
                    <Link to="/agb" className="auth-link">AGB</Link>
                    {' '}zu und nimmst unsere{' '}
                    <Link to="/datenschutz" className="auth-link">Datenschutzbestimmungen</Link>
                    {' '}zur Kenntnis.
                  </p>
                )}

                <button
                  type="submit"
                  className="auth-submit"
                  disabled={laden || (view === 'register' && passwort.length > 0 && passwortScore(passwort) < 5)}
                >
                  {laden
                    ? <span className="auth-spinner" />
                    : view === 'login' ? 'Einloggen' : 'Konto erstellen'}
                </button>

                {view === 'login' && (
                  <div className="auth-link-row" style={{ marginTop: 14 }}>
                    <button type="button" className="auth-link" onClick={() => wechseln('reset')}>
                      Passwort vergessen?
                    </button>
                  </div>
                )}

                {view === 'login' && !darfRegistrieren && (
                  <div className="auth-link-row" style={{ marginTop: 14 }}>
                    <span className="auth-muted">Noch kein Konto?</span>
                    <Link
                      to="/onboarding"
                      className="auth-link"
                      onClick={() => localStorage.removeItem('onboarding_done')}
                    >
                      Kostenlos starten
                    </Link>
                  </div>
                )}
              </form>

              <div className="auth-divider"><span>oder</span></div>

              <button
                className="auth-google"
                onClick={onGoogleLogin}
                disabled={laden}
                type="button"
              >
                <img src="https://www.google.com/favicon.ico" alt="" width="18" height="18" />
                Mit Google {view === 'login' ? 'einloggen' : 'registrieren'}
              </button>

              <button
                className="auth-linkedin"
                onClick={onLinkedInLogin}
                disabled={laden}
                type="button"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
                Mit LinkedIn {view === 'login' ? 'einloggen' : 'registrieren'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
